import React, { useState, useEffect, useCallback } from "react";
import {
  adminGetSettings, adminUpdateSettings,
  adminGetLeaveQuota, adminSetLeaveQuota, adminAddHoliday,
  adminGetHolidays, adminUpdateHoliday, adminDeleteHoliday,
} from "../../../services/attendanceApi";
import Icons from "./Icons";
import { useToast } from "./Toast";

/**
 * AdminSettings — configure system rules (common for all employees):
 * shift hours, grace, break, permission limit, leave quotas, holidays.
 */
function AdminSettings() {
  const [settings, setSettings] = useState({
    shiftStart: "10:00", shiftEnd: "19:00", graceMinutes: 15, breakMinutes: 60,
    permissionMonthlyLimit: 120, halfDayHours: 4, fullDayHours: 8, lateAlertMinutes: 30,
  });
  const [quota, setQuota] = useState({ year: new Date().getFullYear(), clQuota: "", slQuota: "", elQuota: "" });
  const [quotaLoaded, setQuotaLoaded] = useState(false);
  const [holiday, setHoliday] = useState({ holidayDate: "", name: "", type: "GOVERNMENT", isWorking: false });
  const [holidayList, setHolidayList] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  const [editingId, setEditingId] = useState(null);
  const [editHoliday, setEditHoliday] = useState({ holidayDate: "", name: "", type: "GOVERNMENT", isWorking: false });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const { confirm, toast } = useToast();
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminGetSettings();
      const data = res && res.data ? res.data : res;
      if (data && Object.keys(data).length) {
        setSettings((s) => ({ ...s, ...normalize(data) }));
      }
      // Prefill the real current leave quota so the form shows actual
      // saved values instead of misleading hardcoded numbers.
      try {
        const qres = await adminGetLeaveQuota(new Date().getFullYear());
        const qdata = qres && qres.data ? qres.data : qres;
        const q = (qdata && qdata.quotas) ? qdata.quotas : qdata;
        if (q) {
          setQuota((prev) => ({
            ...prev,
            year: qdata.year ?? prev.year,
            clQuota: pickQ(qdata.cl_quota, q.CL, q.cl_quota),
            slQuota: pickQ(qdata.sl_quota, q.SL, q.sl_quota),
            elQuota: pickQ(qdata.el_quota, q.EL, q.el_quota),
          }));
          setQuotaLoaded(true);
        }
      } catch (qerr) {
        // GET quota unavailable — leave fields blank/placeholder.
        setQuotaLoaded(false);
      }
    } catch (err) {
      setLoadFailed(true); // could not read current values — warn before saving defaults
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHolidays = useCallback(async (year) => {
    const y = year ?? holidayYear;
    try {
      setHolidaysLoading(true);
      const res = await adminGetHolidays(y);
      const data = res && res.data ? res.data : res;
      const list = (data && data.holidays) ? data.holidays : (Array.isArray(data) ? data : []);
      setHolidayList(Array.isArray(list) ? list : []);
    } catch (err) {
      // Non-fatal: show empty list; surface a toast for context.
      setHolidayList([]);
      if (toast && toast.error) toast.error(err.message || "Could not load holidays.");
    } finally {
      setHolidaysLoading(false);
    }
  }, [holidayYear, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHolidays(holidayYear); }, [loadHolidays, holidayYear]);

  const flash = (m) => { setMsg(m); setError(""); setTimeout(() => setMsg(""), 3000); };

  const saveSettings = async () => {
    if (loadFailed) {
      const ok = await confirm("Current settings could not be loaded, so the values shown are defaults. Saving will overwrite the saved settings with these values.", "Save default values?");
      if (!ok) return;
    }
    try { await adminUpdateSettings(settings); flash("Settings saved."); }
    catch (err) { setError(err.message || "Could not save settings."); }
  };
  const saveQuota = async () => {
    const ok = await confirm(
      `This will OVERWRITE the leave balance quota for ALL employees for ${quota.year}. Are you sure?`,
      "Apply quota to all?"
    );
    if (!ok) return;
    try { await adminSetLeaveQuota(quota); flash("Leave quotas applied to all employees."); }
    catch (err) { setError(err.message || "Could not set quota."); }
  };
  const saveHoliday = async () => {
    if (!holiday.holidayDate || !holiday.name) { setError("Enter holiday date and name."); return; }
    try {
      await adminAddHoliday(holiday);
      flash("Holiday added.");
      setHoliday({ holidayDate: "", name: "", type: "GOVERNMENT", isWorking: false });
      await loadHolidays(holidayYear);
    }
    catch (err) { setError(err.message || "Could not add holiday."); }
  };

  const startEditHoliday = (h) => {
    setEditingId(h.id);
    setEditHoliday({
      holidayDate: (h.holiday_date || h.holidayDate || "").slice(0, 10),
      name: h.name || "",
      type: h.type || "GOVERNMENT",
      isWorking: (h.is_working ?? h.isWorking) === true,
    });
  };

  const cancelEditHoliday = () => {
    setEditingId(null);
    setEditHoliday({ holidayDate: "", name: "", type: "GOVERNMENT", isWorking: false });
  };

  const saveEditHoliday = async (id) => {
    if (!editHoliday.holidayDate || !editHoliday.name) { setError("Enter holiday date and name."); return; }
    try {
      await adminUpdateHoliday(id, editHoliday);
      flash("Holiday updated.");
      cancelEditHoliday();
      await loadHolidays(holidayYear);
    }
    catch (err) { setError(err.message || "Could not update holiday."); }
  };

  const removeHoliday = async (h) => {
    const label = h.name || h.holiday_date || h.id;
    const ok = await confirm(`Delete holiday "${label}"? This cannot be undone.`, "Delete holiday?");
    if (!ok) return;
    try {
      await adminDeleteHoliday(h.id);
      flash("Holiday deleted.");
      await loadHolidays(holidayYear);
    }
    catch (err) { setError(err.message || "Could not delete holiday."); }
  };

  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setQ = (k, v) => setQuota((q) => ({ ...q, [k]: v }));
  const setH = (k, v) => setHoliday((h) => ({ ...h, [k]: v }));
  const setEH = (k, v) => setEditHoliday((h) => ({ ...h, [k]: v }));

  if (loading) return <div className="atl-skeleton atl-skeleton--tall" />;

  return (
    <div className="atl-settings">
      {msg && <div className="atl-success">{msg}</div>}
      {error && <div className="atl-error">{error}</div>}
      {loadFailed && <div className="atl-warn-inline">Could not load current settings — these are default values. Review them before saving.</div>}

      {/* Shift & Rules */}
      <div className="atl-card atl-card--shift">
        <h3><span className="atl-h3ic">{Icons.gear}</span>Shift &amp; Rules</h3>
        <div className="atl-row">
          <div className="atl-field"><label>Shift Start</label><input type="time" className="atl-input" value={settings.shiftStart} onChange={(e) => setS("shiftStart", e.target.value)} /></div>
          <div className="atl-field"><label>Shift End</label><input type="time" className="atl-input" value={settings.shiftEnd} onChange={(e) => setS("shiftEnd", e.target.value)} /></div>
        </div>
        <div className="atl-row">
          <div className="atl-field"><label>Grace (min)</label><input type="number" className="atl-input" value={settings.graceMinutes} onChange={(e) => setS("graceMinutes", +e.target.value)} /></div>
          <div className="atl-field"><label>Break (min)</label><input type="number" className="atl-input" value={settings.breakMinutes} onChange={(e) => setS("breakMinutes", +e.target.value)} /></div>
        </div>
        <div className="atl-row">
          <div className="atl-field"><label>Permission Limit (min/month)</label><input type="number" className="atl-input" value={settings.permissionMonthlyLimit} onChange={(e) => setS("permissionMonthlyLimit", +e.target.value)} /></div>
          <div className="atl-field"><label>Late Alert (min)</label><input type="number" className="atl-input" value={settings.lateAlertMinutes} onChange={(e) => setS("lateAlertMinutes", +e.target.value)} /></div>
        </div>
        <div className="atl-row">
          <div className="atl-field"><label>Half-day Hours</label><input type="number" className="atl-input" value={settings.halfDayHours} onChange={(e) => setS("halfDayHours", +e.target.value)} /></div>
          <div className="atl-field"><label>Full-day Hours</label><input type="number" className="atl-input" value={settings.fullDayHours} onChange={(e) => setS("fullDayHours", +e.target.value)} /></div>
        </div>
        <button className="atl-btn atl-btn--primary" onClick={saveSettings}>Save Settings</button>
      </div>

      {/* Leave Quota */}
      <div className="atl-card">
        <h3><span className="atl-h3ic">{Icons.quota}</span>Yearly Leave Quota (Common for All)</h3>
        <div className="atl-row atl-row--quota">
          <div className="atl-field"><label>Year</label><input type="number" className="atl-input" value={quota.year} onChange={(e) => setQ("year", +e.target.value)} /></div>
          <div className="atl-field"><label>CL</label><input type="number" className="atl-input" value={quota.clQuota} placeholder={quotaLoaded ? "" : "—"} onChange={(e) => setQ("clQuota", e.target.value === "" ? "" : +e.target.value)} /></div>
          <div className="atl-field"><label>SL</label><input type="number" className="atl-input" value={quota.slQuota} placeholder={quotaLoaded ? "" : "—"} onChange={(e) => setQ("slQuota", e.target.value === "" ? "" : +e.target.value)} /></div>
          <div className="atl-field"><label>EL</label><input type="number" className="atl-input" value={quota.elQuota} placeholder={quotaLoaded ? "" : "—"} onChange={(e) => setQ("elQuota", e.target.value === "" ? "" : +e.target.value)} /></div>
        </div>
        <button className="atl-btn atl-btn--primary" onClick={saveQuota}>Apply Quota to All</button>
      </div>

      {/* Holidays */}
      <div className="atl-card">
        <h3><span className="atl-h3ic">{Icons.calendar}</span>Add Holiday / Alternative Saturday</h3>
        <div className="atl-row">
          <div className="atl-field"><label>Date</label><input type="date" className="atl-input" value={holiday.holidayDate} onChange={(e) => setH("holidayDate", e.target.value)} /></div>
          <div className="atl-field"><label>Name</label><input className="atl-input" value={holiday.name} onChange={(e) => setH("name", e.target.value)} placeholder="e.g. Diwali" /></div>
        </div>
        <div className="atl-row">
          <div className="atl-field"><label>Type</label>
            <select className="atl-input" value={holiday.type} onChange={(e) => setH("type", e.target.value)}>
              <option value="GOVERNMENT">Government</option>
              <option value="COMPANY">Company</option>
              <option value="ALT_SATURDAY_WORKING">Alternative Saturday (Working)</option>
            </select>
          </div>
          <div className="atl-field atl-field--check">
            <label><input type="checkbox" checked={holiday.isWorking} onChange={(e) => setH("isWorking", e.target.checked)} /> Working day</label>
          </div>
        </div>
        <button className="atl-btn atl-btn--primary" onClick={saveHoliday}>Add Holiday</button>

        {/* Existing holidays — list with edit/delete */}
        <div className="atl-holiday-list">
          <div className="atl-row atl-row--quota">
            <div className="atl-field">
              <label>Show Year</label>
              <input type="number" className="atl-input" value={holidayYear} onChange={(e) => setHolidayYear(+e.target.value || new Date().getFullYear())} />
            </div>
            <div className="atl-field atl-field--check">
              <button className="atl-btn" type="button" onClick={() => loadHolidays(holidayYear)}>Refresh</button>
            </div>
          </div>

          {holidaysLoading ? (
            <div className="atl-skeleton" />
          ) : holidayList.length === 0 ? (
            <div className="atl-empty">No holidays configured for {holidayYear}.</div>
          ) : (
            <table className="atl-table">
              <thead>
                <tr><th>Date</th><th>Name</th><th>Type</th><th>Working</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {holidayList.map((h) => (
                  editingId === h.id ? (
                    <tr key={h.id}>
                      <td><input type="date" className="atl-input" value={editHoliday.holidayDate} onChange={(e) => setEH("holidayDate", e.target.value)} /></td>
                      <td><input className="atl-input" value={editHoliday.name} onChange={(e) => setEH("name", e.target.value)} /></td>
                      <td>
                        <select className="atl-input" value={editHoliday.type} onChange={(e) => setEH("type", e.target.value)}>
                          <option value="GOVERNMENT">Government</option>
                          <option value="COMPANY">Company</option>
                          <option value="ALT_SATURDAY_WORKING">Alternative Saturday (Working)</option>
                        </select>
                      </td>
                      <td><input type="checkbox" checked={editHoliday.isWorking} onChange={(e) => setEH("isWorking", e.target.checked)} /></td>
                      <td>
                        <button className="atl-btn atl-btn--primary" type="button" onClick={() => saveEditHoliday(h.id)}>Save</button>
                        <button className="atl-btn" type="button" onClick={cancelEditHoliday}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={h.id}>
                      <td>{(h.holiday_date || h.holidayDate || "").slice(0, 10)}</td>
                      <td>{h.name}</td>
                      <td>{h.type}</td>
                      <td>{(h.is_working ?? h.isWorking) === true ? "Yes" : "No"}</td>
                      <td>
                        <button className="atl-btn" type="button" onClick={() => startEditHoliday(h)}>Edit</button>
                        <button className="atl-btn atl-btn--danger" type="button" onClick={() => removeHoliday(h)}>Delete</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function pickQ(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null) return v;
  }
  return "";
}

function normalize(d) {
  // Time inputs need "HH:MM"; backend may still send "HH:MM:SS".
  const hhmm = (v, def) => {
    const s = v || def;
    return typeof s === "string" && s.length >= 5 ? s.slice(0, 5) : s;
  };
  return {
    shiftStart: hhmm(d.shiftStart || d.shift_start, "10:00"),
    shiftEnd: hhmm(d.shiftEnd || d.shift_end, "19:00"),
    graceMinutes: d.graceMinutes ?? d.grace_minutes ?? 15,
    breakMinutes: d.breakMinutes ?? d.break_minutes ?? 60,
    permissionMonthlyLimit: d.permissionMonthlyLimit ?? d.permission_monthly_limit ?? 120,
    halfDayHours: d.halfDayHours ?? d.half_day_hours ?? 4,
    fullDayHours: d.fullDayHours ?? d.full_day_hours ?? 8,
    lateAlertMinutes: d.lateAlertMinutes ?? d.late_alert_minutes ?? 30,
  };
}

export default AdminSettings;
